import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Stepper,
  Step,
  StepIconProps,
  StepLabel,
  Typography,
  IconButton,
  Grid,
  Popover,
  Checkbox,
  Tooltip,
} from '@mui/material';
import StepConnector, {
  stepConnectorClasses,
} from '@mui/material/StepConnector';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import PrintIcon from '@mui/icons-material/Print';
import { assign } from 'xstate';
import { cloneDeep } from 'lodash';
import InfoIcon from '@mui/icons-material/Info';
import RingVolumeIcon from '@mui/icons-material/RingVolume';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { PowerSettingsNew } from '@mui/icons-material';

import { BaseComponent } from '../Component';
import { MachineContext } from '../..';
import theme from '../../theme/theme';

import styles from './ProgressBar.module.css';
import { CoachEnum } from './CoachEnum';
import { ProgressRing } from './ProgressRing';
import { AutoRefresh } from './AutoRefresh';

export interface Phase {
  reference: string;
  text: string;
}

export interface ProgressBarComponent extends BaseComponent {
  type: 'progressBar';
  props: ProgressBarProps;
}
export interface ProgressBarProps {
  coachingPhases: Phase[];
  currPercent: number;
  currPhaseReference: Phase['reference'];
  coachImage: CoachEnum;
  coachAvatarImage?: string;
  phaseHeight?: number;
  phaseClickHandler?: Function;
  hintMessage?: string;
  printClickHandler?: Function;
  printToolTip?: string;
  endClickHandler?: Function;
  endToolTip?: string;
  callClickHandler?: Function;
  callToolTip?: string;
  autoRefreshHandler?: Function;
  hidePhases?: boolean;
  hideProgressRing?: boolean;
}

//defines styles for the connector between two phases
const ProgressPhaseConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.secondary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.secondary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.secondary.main,
    borderTopWidth: 1,
    borderRadius: 1,
    minHeight: '10px',
    maxHeight: '24px',
  },
}));

//defines styles for the icons of the phases
const ProgressPhaseStepIconRoot = styled('div')<{
  ownerState: { active?: boolean };
}>(({ theme, ownerState }) => ({
  color: theme.palette.secondary.main,
  backgroundColor: 'none',
  borderColor: theme.palette.secondary.main,
  borderWidth: 'thin',
  display: 'flex',
  width: 24,
  alignItems: 'center',
  ...(ownerState.active && {
    color: theme.palette.secondary.main,
  }),
  '& .ProgressPhaseStepIcon-completedIcon': {
    color: theme.palette.secondary.main,
    zIndex: 1,
    fontSize: 24,
  },
  '& .ProgressPhaseStepIcon-circle': {
    width: 24,
    height: 24,
    borderRadius: '50%',
  },
}));

function ProgressPhaseStepIcon(props: StepIconProps) {
  const { active, completed, className } = props;

  return (
    <ProgressPhaseStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <CheckCircleIcon className="ProgressPhaseStepIcon-completedIcon" />
      ) : active ? (
        <CircleIcon className="ProgressPhaseStepIcon-circle" />
      ) : (
        <CircleOutlinedIcon className="ProgressPhaseStepIcon-circle" />
      )}
    </ProgressPhaseStepIconRoot>
  );
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  coachingPhases,
  currPercent,
  currPhaseReference,
  coachImage,
  coachAvatarImage,
  phaseHeight = 275,
  phaseClickHandler,
  endClickHandler,
  endToolTip,
  callClickHandler,
  callToolTip,
  hintMessage = '',
  printClickHandler,
  printToolTip,
  autoRefreshHandler,
  hidePhases = false,
  hideProgressRing = false,
}) => {
  const currPhaseIndex = useMemo(() => {
    return coachingPhases.findIndex((phase) => {
      return phase.reference === currPhaseReference;
    });
  }, [coachingPhases, currPhaseReference]);
  useEffect(() => {
    const element = document.getElementById('scrollArea');
    if (element) {
      element.scrollTop = 64 * (currPhaseIndex - 1);
    }
  }, [currPhaseIndex]);
  const handlePhaseClick = (clickedPhase: any) => {
    if (phaseClickHandler) {
      const phaseIndex = coachingPhases.findIndex((phase) => {
        return phase.reference === clickedPhase.reference;
      });
      phaseClickHandler(clickedPhase.reference, phaseIndex);
    }
  };
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const [hideStepper, setHideStepper] = useState<boolean>(hidePhases);

  return (
    <>
      <div className={styles['progress-bar']}>
        {coachAvatarImage && coachAvatarImage.trim() !== '' ? (
          <img
            src={coachAvatarImage}
            alt="Coach"
            className={styles['coach-other']}
          />
        ) : (
          <div className={`${styles['coach-image']} ${styles[coachImage]}`} />
        )}
        <Card className={styles['progress-card']}>
          <CardContent className={styles['progress-card-content']}>
            <Grid
              container
              direction="row"
              alignItems="flex-end"
              justifyContent="space-between"
              marginBottom="15px"
              gap="20px"
            >
              <Grid item>
                <Tooltip
                  title={printToolTip === undefined ? '' : printToolTip}
                  placement="top"
                >
                  {printClickHandler !== undefined ? (
                    <IconButton onClick={() => printClickHandler()}>
                      <PrintIcon fontSize="small" color="secondary" />
                    </IconButton>
                  ) : (
                    <div style={{ width: '36px' }}></div>
                  )}
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip
                  title={endToolTip === undefined ? '' : endToolTip}
                  placement="top"
                >
                  {endClickHandler !== undefined ? (
                    <IconButton onClick={() => endClickHandler()}>
                      <PowerSettingsNew
                        fontSize="small"
                        color="error"
                        style={{ alignSelf: 'center', justifySelf: 'center' }}
                      />
                    </IconButton>
                  ) : (
                    <div style={{ width: '36px' }}></div>
                  )}
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip
                  title={callToolTip === undefined ? '' : callToolTip}
                  placement="top"
                >
                  {callClickHandler !== undefined ? (
                    <IconButton onClick={() => callClickHandler()}>
                      <RingVolumeIcon fontSize="small" color="secondary" />
                    </IconButton>
                  ) : (
                    <div style={{ width: '36px' }}></div>
                  )}
                </Tooltip>
              </Grid>
            </Grid>

            {hideProgressRing !== true && <ProgressRing value={currPercent} />}
            {hideStepper === false ? (
              <div
                className={styles['progress-steps']}
                style={{
                  maxHeight: phaseHeight,
                  marginTop: '15px',
                  width: '100%',
                }}
                id="scrollArea"
              >
                <Stepper
                  activeStep={currPhaseIndex}
                  orientation="vertical"
                  connector={<ProgressPhaseConnector />}
                >
                  {coachingPhases.map((phase) => (
                    <Step key={phase.reference}>
                      <StepLabel
                        onClick={() => handlePhaseClick(phase)}
                        sx={{
                          cursor: 'pointer',
                          padding: '2px 0 2px 0',
                          color: theme.palette.secondary.main,
                        }}
                        StepIconComponent={ProgressPhaseStepIcon}
                        className={styles['step-label']}
                      >
                        {phase.text}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </div>
            ) : (
              <></>
            )}
          </CardContent>

          <Grid
            container
            direction="row"
            alignItems="flex-end"
            justifyContent="space-between"
          >
            <Grid item>
              {autoRefreshHandler !== undefined ? (
                <AutoRefresh refreshHandler={autoRefreshHandler} />
              ) : (
                <div style={{ width: '36px' }}></div>
              )}
            </Grid>
            <Grid item>
              <Checkbox
                sx={{
                  width: '45px',
                }}
                checkedIcon={<ExpandMore fontSize="small" color="secondary" />}
                icon={<ExpandLess fontSize="small" color="secondary" />}
                checked={!!hideStepper}
                onClick={() => {
                  setHideStepper(!hideStepper);
                }}
              />
            </Grid>
            <Grid item style={{ paddingLeft: '8px', paddingRight: '8px' }}>
              <Typography
                aria-owns={open ? id : undefined}
                aria-haspopup="true"
                onMouseEnter={(event: any) => {
                  setAnchorEl(event.currentTarget);
                }}
                onMouseLeave={() => {
                  setAnchorEl(null);
                }}
              >
                <InfoIcon fontSize="small" color="secondary" />{' '}
              </Typography>
            </Grid>
          </Grid>
        </Card>
      </div>
      <Popover
        id={id}
        sx={{
          pointerEvents: 'none',
        }}
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        disableRestoreFocus
      >
        <Typography sx={{ p: 1, maxWidth: '150px', fontSize: '12px' }}>
          {hintMessage}
        </Typography>
      </Popover>
    </>
  );
};

/** define phase anchors
 * this element is used to create an anchor as link target when clicking on phases
 * does not visualize anything
 */
export interface PhaseAnchorComponent extends BaseComponent {
  type: 'phaseAnchor';
  props: PhaseAnchorProps;
}
export interface PhaseAnchorProps {
  phaseReference: string;
}
export const PhaseAnchor: React.FC<PhaseAnchorProps> = ({ phaseReference }) => {
  return (
    <div style={{ height: '1px' }} id={phaseReference}>
      &nbsp;
    </div>
  );
};

/** setProgressPhaseAction
 * set the current phase as triggered by statemachine
 * @param {string} phase reference as described in payload.phase
 */
export const setProgressPhaseAction = assign(
  (context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const currPhase = actionMetadata.action.payload.phase;
    newContext.metaData.currentModulePhase = currPhase;

    newContext.widgetData.push({
      type: 'phaseAnchor',
      temporary: false,
      props: {
        phaseReference: currPhase,
      },
    });

    return newContext;
  }
);
/** setProgressPercentAction
 * set progress to value triggered by statemachine
 * percentage behavior:
    
     1) check for undefined:
     in builder, the default value is set to undefined
     in player, if value is set to undefined, we skip updating the progressbar
     by that, you can keep the percentage value by just not editing anything in builder
    
     2) prevent fallback to zero
     if we are larger than 0, we can't fall back to zero, but we can decrease the
     value to a non-zero value.
     if we are larger than 0 and a 0 value arrives, we ignore this 0
  
  * @param {number} percent as value between 1 and 100 in payload.progressPercent 
 */
export const setProgressPercentAction = assign(
  (context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    const payload = actionMetadata.action.payload;

    const percent = payload.progressPercent
      ? payload.progressPercent
      : payload.percent;

    if (
      percent !== undefined &&
      !(percent === 0 && newContext.metaData.currentModuleProgressPercent > 0)
    ) {
      newContext.metaData.currentModuleProgressPercent = parseInt(percent);
    }

    return newContext;
  }
);
